import { InvestmentAccount } from '@/types/investments';

// TODO: have this fund categories seeded in the database and retrieve them via an API
export type MutualFundCategory = 'equity' | 'debt' | 'hybrid' | 'index' | 'sectoral';

// TODO: have this fund risk ratings seeded in the database and retrieve them via an API
// This will apply to all risk ratings across all investments, so, the table should instead be `risk_rating`
// So, it can be extended to all other investment types
export type MutualFundRiskRating = 'low' | 'moderate' | 'high';

/**
 * Metadata specific to a mutual fund investment account.
 * Stored in the mutual_fund_metadata table.
 */
export interface MutualFundMetadata {
  id: string;
  investment_account_id: string;
  fund_category: MutualFundCategory;
  risk_rating: MutualFundRiskRating;
  benchmark_index: string | null;
  isin: string | null;
  units_held: number;
  unit_cost_basis: number;
  current_nav: number;
  nav_date: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * A single NAV history entry for a mutual fund.
 * Stored in the mutual_fund_nav_history table.
 */
export interface MutualFundNavEntry {
  id: string;
  investment_account_id: string;
  nav_per_unit: number;
  nav_date: string;
  recorded_at: string;
}

/**
 * Full mutual fund record: investment account + metadata joined.
 */
export interface MutualFund extends InvestmentAccount {
  mutual_fund_metadata?: MutualFundMetadata | null;
  /** Populated when currency conversion is applied. */
  convertedBalance?: number;
}

/**
 * Form data for creating or editing a mutual fund investment.
 */
export interface MutualFundFormData {
  // Base investment account fields
  fund_name: string;
  fund_manager: string;
  account_number: string;
  currency: string;
  interest_rate: number;
  starting_amount: number;
  notes?: string;
  description?: string;

  // Mutual-fund-specific fields
  fund_category: MutualFundCategory;
  risk_rating: MutualFundRiskRating;
  benchmark_index?: string;
  isin?: string;
  units_held: number;
  unit_cost_basis: number;
  current_nav: number;
  nav_date?: string;
}

/**
 * Derived performance metrics computed from metadata — never stored.
 */
export interface MutualFundMetrics {
  totalValue: number;
  totalCost: number;
  unrealizedGain: number;
  returnPercent: number;
}

export function computeMutualFundMetrics(metadata: MutualFundMetadata): MutualFundMetrics {
  const totalValue = metadata.units_held * metadata.current_nav;
  const totalCost = metadata.units_held * metadata.unit_cost_basis;
  const unrealizedGain = totalValue - totalCost;
  const returnPercent =
    totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
  return { totalValue, totalCost, unrealizedGain, returnPercent };
}
